import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';

const LEVEL_LABELS = { debug: 'Debug', info: 'Info', warning: 'Aviso', error: 'Erro', critical: 'Crítico' };

const selectSx = {
  height: '40px',
  borderRadius: '8px',
  fontSize: '0.875rem',
  minWidth: 140,
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
};

const textSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    height: '40px',
    '& fieldset': { borderColor: '#E0E0E0' },
    '&:hover fieldset': { borderColor: '#BDBDBD' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
  '& input': { fontSize: '0.875rem' },
};

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' */
function formatDayLabel(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * LogFilters - Barra de filtros da tela de Logs (Admin Geral)
 * @param {Object} props
 * @param {Array<{date: string}>} props.dayOptions
 * @param {string} props.date
 * @param {Function} props.onDateChange
 * @param {Array<string>} props.channels - canais existentes no dia selecionado
 * @param {string} props.channel
 * @param {Function} props.onChannelChange
 * @param {Array<string>} props.levels
 * @param {string} props.level - nível mínimo
 * @param {Function} props.onLevelChange
 * @param {string} props.companyInput
 * @param {Function} props.onCompanyInputChange
 * @param {string} props.reqInput
 * @param {Function} props.onReqInputChange
 * @param {string} props.searchInput
 * @param {Function} props.onSearchInputChange
 * @param {boolean} props.isToday
 * @param {boolean} props.autoRefreshPaused
 * @param {Function} props.onToggleAutoRefresh
 */
function LogFilters({
  dayOptions = [],
  date,
  onDateChange,
  channels = [],
  channel,
  onChannelChange,
  levels = [],
  level,
  onLevelChange,
  companyInput,
  onCompanyInputChange,
  reqInput,
  onReqInputChange,
  searchInput,
  onSearchInputChange,
  isToday,
  autoRefreshPaused,
  onToggleAutoRefresh,
}) {
  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        borderBottom: '1px solid #E8ECF0',
        bgcolor: '#fff',
      }}
    >
      <TextField
        size="small"
        placeholder="Buscar em mensagem ou endpoint"
        value={searchInput}
        onChange={(e) => onSearchInputChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1, minWidth: 220, maxWidth: { sm: 320 }, ...textSx }}
      />

      <Select size="small" value={date || ''} onChange={(e) => onDateChange(e.target.value)} sx={{ ...selectSx, minWidth: 120 }}>
        {dayOptions.map((d) => (
          <MenuItem key={d.date} value={d.date}>{formatDayLabel(d.date)}</MenuItem>
        ))}
      </Select>

      <Select size="small" value={channel || ''} onChange={(e) => onChannelChange(e.target.value)} sx={selectSx}>
        <MenuItem value="">Todos os canais</MenuItem>
        {channels.map((c) => (
          <MenuItem key={c} value={c}>{c}</MenuItem>
        ))}
      </Select>

      <Select size="small" value={level || ''} onChange={(e) => onLevelChange(e.target.value)} sx={selectSx}>
        <MenuItem value="">Todos os níveis</MenuItem>
        {levels.map((lvl) => (
          <MenuItem key={lvl} value={lvl}>{LEVEL_LABELS[lvl] || lvl}</MenuItem>
        ))}
      </Select>

      <TextField
        size="small"
        placeholder="Empresa"
        value={companyInput}
        onChange={(e) => onCompanyInputChange(e.target.value.replace(/[^0-9]/g, ''))}
        sx={{ width: 100, ...textSx }}
      />

      <TextField
        size="small"
        placeholder="Req"
        value={reqInput}
        onChange={(e) => onReqInputChange(e.target.value)}
        sx={{ width: 130, ...textSx }}
      />

      {isToday && (
        <Tooltip title={autoRefreshPaused ? 'Retomar atualização automática' : 'Pausar atualização automática'}>
          <IconButton size="small" onClick={onToggleAutoRefresh} sx={{ ml: 'auto' }}>
            {autoRefreshPaused ? <PlayArrowOutlinedIcon fontSize="small" /> : <PauseOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default LogFilters;
